import { useState, type CSSProperties, type ReactNode } from 'react';
import { useAddSmsOptOut, useRemoveSmsOptOut, useSmsOptOuts, type SmsOptOut } from '../../api/sms-suppressions.api';
import { useOrgSettings, useUpdateOrgSettings } from '../../api/org-settings.api';
import { SMS_OPT_OUT_FOOTER } from '../../lib/sms-text';

/**
 * Text opt-outs (JT1-1857): the agency-wide list of numbers that won't get campaign texts, and
 * the "Reply STOP to opt out" line. Kept apart from the email suppression lists. STOP replies
 * land here on their own; admins can add a number by hand and remove one with a reason.
 */
export function SmsOptOutsSection() {
  const { data: rows, isLoading } = useSmsOptOuts();
  const footerOn = useOrgSettings().data?.data?.sms_opt_out_footer ?? true;
  const updateSettings = useUpdateOrgSettings();
  const [removing, setRemoving] = useState<SmsOptOut | null>(null);

  return (
    <section style={shellStyle}>
      <header style={{ padding: 'var(--space-5) var(--space-6)', borderBottom: '1px solid var(--color-border-default)' }}>
        <h2 style={titleStyle}>Text opt-outs</h2>
        <p style={subtitleStyle}>
          Numbers on this list get no campaign texts from anyone in your agency. Someone who replies STOP is added
          automatically. Email is not affected.
        </p>
      </header>
      <div style={{ padding: 'var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        <label style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>
          <input
            type="checkbox"
            checked={footerOn}
            disabled={updateSettings.isPending}
            onChange={(e) => updateSettings.mutate({ sms_opt_out_footer: e.target.checked })}
            aria-label={`End every campaign text with "${SMS_OPT_OUT_FOOTER}"`}
          />
          <span>
            <strong>End every campaign text with &ldquo;{SMS_OPT_OUT_FOOTER}&rdquo;</strong>
            <br />
            <span style={{ color: 'var(--color-text-muted)' }}>
              Recommended. Texts that already explain how to opt out don&rsquo;t get it twice.
            </span>
          </span>
        </label>

        <AddOptOutForm />

        {isLoading && <div style={mutedStyle}>Loading…</div>}
        {!isLoading && (rows?.length ?? 0) === 0 && <div style={emptyStyle}>No one has opted out of texts.</div>}
        {(rows?.length ?? 0) > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>
            <thead>
              <tr>
                <Th>Phone</Th>
                <Th>How</Th>
                <Th>When</Th>
                <Th>Note</Th>
                <Th>{' '}</Th>
              </tr>
            </thead>
            <tbody>
              {rows!.map((r) => (
                <tr key={r.id}>
                  <Td><code style={{ fontSize: 'var(--text-xs)' }}>{r.phone}</code></Td>
                  <Td>{r.added_by ? `${r.how} by ${r.added_by}` : r.how}</Td>
                  <Td>{new Date(r.opted_out_at).toLocaleString()}</Td>
                  <Td>{r.note ?? <span style={{ color: 'var(--color-text-muted)' }}>-</span>}</Td>
                  <Td>
                    <button type="button" onClick={() => setRemoving(r)} style={smallButtonStyle}>
                      Remove
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {removing && <RemoveOptOutDialog row={removing} onClose={() => setRemoving(null)} />}
    </section>
  );
}

function AddOptOutForm() {
  const add = useAddSmsOptOut();
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    try {
      await add.mutateAsync({ phone: phone.trim(), ...(note.trim() ? { note: note.trim() } : {}) });
      setPhone('');
      setNote('');
    } catch (err) {
      setError((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Could not add the number.');
    }
  };

  return (
    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <Field label="Phone number">
        <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0412 345 678" style={inputStyle} />
      </Field>
      <Field label="Note (optional)">
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Asked on a call" style={{ ...inputStyle, minWidth: '240px' }} />
      </Field>
      <button type="button" onClick={submit} disabled={!phone.trim() || add.isPending} style={primaryButtonStyle(!phone.trim() || add.isPending)}>
        Add opt-out
      </button>
      {error && <div role="alert" style={{ width: '100%', color: 'var(--color-text-danger)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)' }}>{error}</div>}
    </div>
  );
}

function RemoveOptOutDialog({ row, onClose }: { row: SmsOptOut; onClose: () => void }) {
  const remove = useRemoveSmsOptOut();
  const [reason, setReason] = useState('');
  const optedOutThemselves = row.reason === 'STOP' || row.reason === 'PROVIDER_OPT_OUT';

  const confirm = async () => {
    await remove.mutateAsync({ id: row.id, reason: reason.trim() });
    onClose();
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Remove text opt-out" style={overlayStyle}>
      <div style={dialogStyle}>
        <h3 style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-lg)', color: 'var(--color-text-brand)' }}>
          Remove {row.phone} from text opt-outs?
        </h3>
        <p style={{ ...mutedStyle, marginTop: 'var(--space-2)' }}>
          Campaigns will be able to text this number again. The reason is kept in the audit log.
          {optedOutThemselves &&
            ' They opted out themselves, so your text provider keeps blocking them until they text START to your number.'}
        </p>
        <Field label="Reason">
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="They asked to hear from us again" style={{ ...inputStyle, width: '100%' }} />
        </Field>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
          <button type="button" onClick={onClose} style={smallButtonStyle}>Cancel</button>
          <button type="button" onClick={confirm} disabled={!reason.trim() || remove.isPending} style={primaryButtonStyle(!reason.trim() || remove.isPending)}>
            Remove opt-out
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
      {label}
      {children}
    </label>
  );
}

function Th({ children }: { children: ReactNode }) {
  return <th style={{ textAlign: 'left', padding: 'var(--space-2) var(--space-3)', borderBottom: '1px solid var(--color-border-default)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)' }}>{children}</th>;
}

function Td({ children }: { children: ReactNode }) {
  return <td style={{ padding: 'var(--space-2) var(--space-3)', borderBottom: '1px solid var(--color-border-default)', color: 'var(--color-text-primary)' }}>{children}</td>;
}

const shellStyle: CSSProperties = { backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 'var(--card-radius)', boxShadow: 'var(--card-shadow)', overflow: 'hidden' };
const titleStyle: CSSProperties = { margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)' };
const subtitleStyle: CSSProperties = { marginTop: 'var(--space-1)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' };
const mutedStyle: CSSProperties = { fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' };
const emptyStyle: CSSProperties = { padding: 'var(--space-6)', border: '1px dashed var(--color-border-strong)', borderRadius: 'var(--radius-input)', textAlign: 'center', ...mutedStyle };
const inputStyle: CSSProperties = { height: 'var(--input-height)', padding: '0 var(--space-3)', border: '1px solid var(--input-border)', borderRadius: 'var(--input-radius)', fontFamily: 'var(--font-sans)', fontSize: 'var(--input-font-size)', color: 'var(--color-text-primary)', backgroundColor: 'var(--input-bg)' };
const smallButtonStyle: CSSProperties = { padding: 'var(--space-1) var(--space-3)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--btn-radius)', background: 'transparent', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', cursor: 'pointer' };
const primaryButtonStyle = (disabled: boolean): CSSProperties => ({
  height: 'var(--input-height)', padding: '0 var(--space-4)', border: 'none', borderRadius: 'var(--btn-radius)',
  backgroundColor: 'var(--color-action-primary)', color: 'var(--color-action-primary-text)', fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
});
const overlayStyle: CSSProperties = { position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 };
const dialogStyle: CSSProperties = { width: 'min(480px, 92vw)', backgroundColor: 'var(--card-bg)', borderRadius: 'var(--card-radius)', padding: 'var(--space-6)', boxShadow: 'var(--card-shadow)' };
